/*
 * The size of a relationship label: an EdgeLabel at a fixed width, so a long
 * label wraps to more lines and grows down. React Flow measures a label only
 * after it renders, so layouts use these values to reserve its space first.
 * The presentation diagrams and the diagram builder share them.
 */

// The fixed label width, in canvas pixels. Long labels wrap at this width.
export const RELATIONSHIP_NODE_WIDTH = 160;
// Matches the rendered EdgeLabel height: 3px border + 4px padding + 16px
// (text-secondary line-height) + 4px padding + 3px border. Keeping declared
// size equal to the visible element lets helper lines align with the
// EdgeLabel's edges while arrow edges still terminate flush with its border.
export const RELATIONSHIP_NODE_HEIGHT = 30;

// EdgeLabel uses 8px horizontal padding and 1px borders on each side, so
// roughly 142px of the 160px node width is available for text.
const RELATIONSHIP_LABEL_AVAILABLE_TEXT_WIDTH_PX = 142;
// `text-secondary` is 12px Inter; ~6.5px is a slightly conservative average
// glyph width for that font/size, which biases the estimate toward more
// lines (and so more reserved vertical space) than a perfectly tight fit.
const RELATIONSHIP_LABEL_AVG_CHAR_WIDTH_PX = 6.5;
// `text-secondary` line-height is 16px (see styles/tokens/fonts.ts).
const RELATIONSHIP_LABEL_LINE_HEIGHT_PX = 16;
// 4px top + 4px bottom padding plus 1px border on each side from EdgeLabel.
const RELATIONSHIP_LABEL_VERTICAL_CHROME_PX = 10;

/**
 * Returns the box that a layout reserves for a relationship label: the fixed
 * width and the estimated height of the wrapped text.
 */
export function getRelationshipLabelSize(label?: string | null): {
  width: number;
  height: number;
} {
  return {
    width: RELATIONSHIP_NODE_WIDTH,
    height: estimateRelationshipLabelHeight(label),
  };
}

/**
 * Approximate the rendered EdgeLabel height for a relationship-label node.
 *
 * The label width is fixed (RELATIONSHIP_NODE_WIDTH), so multi-line labels
 * grow vertically. React Flow only learns the real height after measurement,
 * which happens *after* tidy-tree layout has already chosen Y positions.
 * Without this estimate, the layout reserves only RELATIONSHIP_NODE_HEIGHT
 * (one line) and tall labels overlap the row below them.
 *
 * The estimate simulates a greedy word-wrap using an average glyph width for
 * `text-secondary` (12px) and rounds up so it's a slight over-estimate, which
 * is what we want for a layout reservation.
 */
export function estimateRelationshipLabelHeight(label?: string | null): number {
  if (!label || typeof label !== 'string' || label.trim().length === 0) {
    return RELATIONSHIP_NODE_HEIGHT;
  }

  const charsPerLine = Math.max(
    1,
    Math.floor(
      RELATIONSHIP_LABEL_AVAILABLE_TEXT_WIDTH_PX /
        RELATIONSHIP_LABEL_AVG_CHAR_WIDTH_PX
    )
  );

  const words = label.trim().split(/\s+/);
  let lineCount = 1;
  let currentLineLength = 0;

  for (const word of words) {
    // A single word longer than a line still occupies (at least) one line —
    // CSS will break it visually, but for vertical reservation rounding up
    // by `ceil(len / charsPerLine)` is safe.
    if (word.length > charsPerLine) {
      if (currentLineLength > 0) {
        lineCount += 1;
      }
      lineCount += Math.ceil(word.length / charsPerLine) - 1;
      currentLineLength = word.length % charsPerLine || charsPerLine;
      continue;
    }

    const lengthWithSpace =
      currentLineLength === 0
        ? word.length
        : currentLineLength + 1 + word.length;
    if (lengthWithSpace > charsPerLine) {
      lineCount += 1;
      currentLineLength = word.length;
    } else {
      currentLineLength = lengthWithSpace;
    }
  }

  const estimated =
    lineCount * RELATIONSHIP_LABEL_LINE_HEIGHT_PX +
    RELATIONSHIP_LABEL_VERTICAL_CHROME_PX;

  return Math.max(RELATIONSHIP_NODE_HEIGHT, estimated);
}
